import {SIDEBAR_LOCALSTORAGEKEY} from 'constants/local-storage-keys';
import {Actions} from 'constants/permissions/actions';
import {Features} from 'constants/plan-access/features';
import {Plans} from 'constants/plans';
import {routeNames} from 'constants/route-names';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import useBeatsEnabled from 'ui/@hooks/use-beats-enabled';
import {NavLink, useLocation} from 'react-router-dom';
import IconChevronLeft from 'ui/@_components/kit/icons/chevron-left.svg?react';
import IconChevronRight from 'ui/@_components/kit/icons/chevron-right.svg?react';
import useDemoAccountDetails from 'ui/@hooks/use-demo-account-details';
import useHasPlanAccess from 'ui/@hooks/use-has-plan-access';
import HasPermission from '../has-permission';
import FreeTrailCard from './@components/free-trail-card';
import ItemLock from './@components/item-lock';
import IconActionSteps from './assets/action-steps.svg?react';
import IconAssessmentPoll from './assets/assessments-polls.svg?react';
import IconCulture from './assets/culture.svg?react';
import IconDashboard from './assets/dashboard.svg?react';
import IconFlow from './assets/flow.svg?react';
import IconGettingStarted from './assets/getting-started.svg?react';
import IconPeopleTeams from './assets/people.svg?react';
import styles from './styles.module.scss';

const Navigation = () => {
  const {t} = useTranslation('application', {
    keyPrefix: 'components.navigation.links',
  });

  const {currentPlans} = useHasPlanAccess();
  const {isBeatsEnabled} = useBeatsEnabled();
  const assessmentsLabel = t(isBeatsEnabled ? 'tune-ins' : 'assessment-polls');

  const {flowBranding, primaryLogo} = useDemoAccountDetails();

  const {
    dashboardRoute,
    senseRoute,
    applicationRoute,
    cultureRoute,
    assessmentPollRoute,
    peopleTeamsRoute,
    actionStepsRoute,
    gettingStartedRoute,
  } = routeNames;

  const location = useLocation();

  const getInitialState = () => {
    if (location.state?.isFirstVisit) return !!location.state.isFirstVisit;

    const sidebarLocalStorageStatus = parseInt(
      localStorage.getItem(SIDEBAR_LOCALSTORAGEKEY) || '1'
    );

    return !!sidebarLocalStorageStatus;
  };

  const [isOpen, setIsOpen] = useState(getInitialState());

  const onToggle = () => {
    setIsOpen((prev) => {
      if (prev) {
        localStorage.setItem(SIDEBAR_LOCALSTORAGEKEY, '0');
      } else {
        localStorage.setItem(SIDEBAR_LOCALSTORAGEKEY, '1');
      }

      return !prev;
    });
  };

  return (
    <div className={isOpen ? styles.container : styles.closedContainer}>
      <div className={isOpen ? styles.openHeader : styles.closedHeader}>
        <NavLink to={`/${applicationRoute}`}>
          <img
            width="40px"
            height="24px"
            src={primaryLogo}
            alt="logo"
            className={styles.logo}
          />
        </NavLink>

        <button className={styles.button} onClick={onToggle}>
          {isOpen ? <IconChevronLeft /> : <IconChevronRight />}
        </button>
      </div>

      <ItemLock
        feature={null}
        icon={<IconGettingStarted />}
        link={`/${applicationRoute}/${gettingStartedRoute}`}
        name={t('getting-started')}
        tooltipText={isOpen ? null : t('getting-started')}
        isSidebarOpen={isOpen}
      />

      <div className={styles.section}>{t('measure')}</div>

      <ItemLock
        feature={null}
        icon={<IconDashboard />}
        link={`/${applicationRoute}/${dashboardRoute}`}
        name={t('cultureKPI')}
        tooltipText={isOpen ? null : t('cultureKPI')}
        isSidebarOpen={isOpen}
      />

      <ItemLock
        feature={Features.ASSESSMENTS_POLLS}
        icon={<IconAssessmentPoll />}
        link={`/${applicationRoute}/${assessmentPollRoute}`}
        name={assessmentsLabel}
        tooltipText={isOpen ? null : assessmentsLabel}
        isSidebarOpen={isOpen}
      />

      <div className={styles.section}>{t('build')}</div>

      <ItemLock
        feature={Features.VALUES_DASHBOARD}
        icon={<IconCulture />}
        link={`/${applicationRoute}/${cultureRoute}`}
        name={t('values')}
        tooltipText={isOpen ? null : t('values')}
        isSidebarOpen={isOpen}
      />

      <ItemLock
        feature={Features.PEOPLE_AND_TEAMS}
        icon={<IconPeopleTeams />}
        link={`/${applicationRoute}/${peopleTeamsRoute}`}
        name={t('people')}
        tooltipText={isOpen ? null : t('people')}
        isSidebarOpen={isOpen}
      />

      <div className={styles.section}>{t('advance')}</div>

      <ItemLock
        feature={Features.ACTION_STEPS}
        icon={<IconActionSteps />}
        link={`/${applicationRoute}/${actionStepsRoute}`}
        name={t('action-steps')}
        tooltipText={isOpen ? null : t('action-steps')}
        isSidebarOpen={isOpen}
      />

      <ItemLock
        feature={Features.INSTILL_FLOW}
        icon={<IconFlow />}
        link={`/${applicationRoute}/${senseRoute}`}
        name={flowBranding}
        tooltipText={isOpen ? null : flowBranding}
        isSidebarOpen={isOpen}
      />

      <HasPermission action={Actions.UPDATE_PLAN}>
        {isOpen && currentPlans.includes(Plans.FREE_TRAIL_PLAN) && (
          <div className={styles.bottomCard}>
            <FreeTrailCard />
          </div>
        )}
      </HasPermission>
    </div>
  );
};

export default Navigation;
